'use client'

import { useCallback } from 'react'
import { useAnalytics } from './useAnalytics'
import { MTAG_EVENTS, SOURCE_APP } from '../config/eventNames'
import { omitEmpty } from '../utils/omitEmpty'
import type { ProductListFiltered } from '@material-bank/analytics-events-ts/mtag'

export type ProductListFilteredProduct = ProductListFiltered['products'][number]

const LOCALE_PREFIX = /^\/([a-z]{2}-[A-Z]{2})(?=\/|$)/

// `url` is required on each product in this event's schema. MTag has no standalone PDP —
// a tile opens the add-to-cart sheet — so the drilled-in product route is the closest
// thing to a canonical address for a material.
//
// The locale prefix is carried over from the route the user is actually on rather than
// added unconditionally: next-intl runs `localePrefix: 'as-needed'`, so the default locale
// has no prefix and hardcoding one would point every en-US event at a path that redirects.
export function buildProductUrl(tagRef: string, sku: string): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const locale = window.location.pathname.match(LOCALE_PREFIX)?.[1]
  const prefix = locale ? `/${locale}` : ''

  return `${window.location.origin}${prefix}/tag/${tagRef}/product/${sku}`
}

type ProductListFilteredInput = {
  category: ProductListFiltered['category']
  viewMode: ProductListFiltered['view_mode']
  sort?: ProductListFiltered['sort']
  filters: ProductListFiltered['filters']
  products: ProductListFilteredProduct[]
  url?: string
}

// Fires the canonical `product_list_filtered` for a list whose filter or sort the user
// just changed. Unlike `product_list_viewed` this is not viewport-driven — the change is
// the event — so callers invoke it directly from the control's handler with the product
// set the change produces.
export function useProductListFiltered() {
  const { track } = useAnalytics()

  return useCallback(
    (input: ProductListFilteredInput) => {
      const payload = omitEmpty({
        category: input.category,
        filters: input.filters,
        sort: input.sort,
        view_mode: input.viewMode,
        results_count: input.products.length,
        products: input.products,
        source_app: SOURCE_APP,
        url:
          input.url ??
          (typeof window !== 'undefined' ? window.location.href : ''),
      }) as ProductListFiltered

      track(MTAG_EVENTS.PRODUCT_LIST_FILTERED, payload)
    },
    [track],
  )
}
